import DataGrid, { Column, Item, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import SelectBox from 'devextreme-react/select-box';
import { useMemo, useRef } from 'react';
import type { ChartThemeColors } from '../../charts/useChartThemeColors';
import { OPS_SITE_GRID_PROPS, OpsGridToolbarItems, OpsSiteGridChrome } from '../../opsDataGrid';
import { NamedBreakdownBarChart, NamedShareDonut } from '../userActivityCharts';
import type { UserActivityDimensionRow, UserActivityReportMetric } from '../types';
import { ReportChartCard } from './ReportChartCard';

const METRIC_OPTIONS: { value: UserActivityReportMetric; text: string }[] = [
  { value: 'sessions', text: 'Sessions' },
  { value: 'uniqueUsers', text: 'Unique users' },
  { value: 'totalMinutes', text: 'Total minutes' }
];

export function DimensionReportPanel({
  title,
  subtitle,
  rows,
  colors,
  metric,
  onMetricChange,
  onRowDrilldown,
  fileNamePrefix,
  emptyMessage
}: {
  title: string;
  subtitle?: string;
  rows: UserActivityDimensionRow[];
  colors: ChartThemeColors;
  metric: UserActivityReportMetric;
  onMetricChange?: (metric: UserActivityReportMetric) => void;
  onRowDrilldown?: (row: UserActivityDimensionRow) => void;
  fileNamePrefix: string;
  emptyMessage?: string;
}) {
  const gridRef = useRef<DataGridRef>(null);
  const empty = rows.length === 0 || rows.every((r) => r.sessions === 0);

  const toolbar = useMemo(
    () =>
      onMetricChange ? (
        <SelectBox
          dataSource={METRIC_OPTIONS}
          value={metric}
          valueExpr="value"
          displayExpr="text"
          width={160}
          onValueChanged={(e) => onMetricChange(e.value as UserActivityReportMetric)}
        />
      ) : null,
    [metric, onMetricChange]
  );

  return (
    <div className="user-activity-dimension-report">
      <div className="user-activity-analytics__body--split">
        <ReportChartCard title={title} subtitle={subtitle} empty={empty} emptyMessage={emptyMessage} toolbar={toolbar}>
          <div className="user-activity-analytics__chart-host">
            <NamedBreakdownBarChart
              data={rows}
              colors={colors}
              valueField={metric}
              fileName={`${fileNamePrefix}-bar`}
            />
          </div>
        </ReportChartCard>
        <ReportChartCard title="Share" empty={empty} emptyMessage={emptyMessage}>
          <div className="user-activity-analytics__chart-host">
            <NamedShareDonut
              data={rows}
              colors={colors}
              valueField={metric}
              fileName={`${fileNamePrefix}-donut`}
            />
          </div>
        </ReportChartCard>
      </div>

      <section className="user-activity-report-card" style={{ marginTop: 12 }}>
        <div className="user-activity-report-card__header">
          <h3 className="ops-section-title" style={{ margin: 0 }}>
            Underlying data
          </h3>
        </div>
        <div className="user-activity-analytics__active-grid">
          <DataGrid
            ref={gridRef}
            dataSource={rows}
            keyExpr="key"
            height="100%"
            showBorders
            columnAutoWidth
            {...OPS_SITE_GRID_PROPS}
            noDataText={emptyMessage ?? 'No rows.'}
            onRowDblClick={(e) => {
              const row = e.data as UserActivityDimensionRow | undefined;
              if (row && onRowDrilldown) onRowDrilldown(row);
            }}
          >
            <OpsSiteGridChrome
              stateStorageKey={`user-activity-dim-${fileNamePrefix}`}
              searchPlaceholder="Filter rows…"
              showPaging={false}
            />
            <Toolbar>
              <OpsGridToolbarItems
                gridRef={gridRef}
                onRefresh={() => gridRef.current?.instance()?.refresh()}
                export={{
                  fileName: `${fileNamePrefix}-rows`,
                  worksheetName: title,
                  hint: 'Export rows'
                }}
              />
              <Item name="searchPanel" location="after" />
            </Toolbar>
            <Column dataField="label" caption="Label" minWidth={160} />
            <Column dataField="sessions" caption="Sessions" width={100} />
            <Column dataField="uniqueUsers" caption="Unique users" width={120} />
            <Column dataField="totalMinutes" caption="Total minutes" width={120} />
            <Column dataField="averageMinutes" caption="Avg minutes" width={110} />
            <Column
              dataField="share"
              caption="Share %"
              width={90}
              calculateDisplayValue={(row: UserActivityDimensionRow) => `${row.share}%`}
            />
            {onRowDrilldown ? (
              <Column
                caption=""
                width={110}
                allowSorting={false}
                cellRender={(c) => (
                  <button
                    type="button"
                    className="user-activity-drilldown-link"
                    onClick={() => onRowDrilldown(c.data as UserActivityDimensionRow)}
                  >
                    View sessions
                  </button>
                )}
              />
            ) : null}
          </DataGrid>
        </div>
      </section>
    </div>
  );
}
